import { MuiColorInput, MuiColorInputColors } from "mui-color-input";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { TabPanel } from "../../../../../../../components/TabPanel";
import { LineMetadata, Shape } from "../../RampCanvasV2";
import { FILL_PATTERNS } from "../../patterns/usePatterns";
import { LINE_STYLES, PREFIXED_COLORS, TEXT_POSITION_OPTIONS } from "../constants";

type Props = {
  value: number;
  index: number;
  shape: Shape | undefined;
  shapes: Shape[];
  activeShapes: string[];
  contextMenu: { x: number; y: number; shapeId: string } | null;
  setShapes: (shapes: Shape[]) => void;
  setSelectedShapes: (attribute: string, value: any) => void;
  setSelectedShapesMultiple: (attributes: Record<string, any>) => void;
};

export const StylingTab: React.FC<Props> = ({
  value,
  index,
  shape,
  shapes,
  activeShapes,
  contextMenu,
  setShapes,
  setSelectedShapes,
  setSelectedShapesMultiple,
}) => {
  const { t } = useTranslation();

  if (!shape) return null;

  const prefixedValue = PREFIXED_COLORS.find(
    (option) =>
      option.stroke_style === shape.stroke_style &&
      option.fill_style === shape.fill_style
  )?.label;

  return (
    <TabPanel value={value} index={index}>
      <Stack direction="column" spacing={1}>
        <TextField
          size="small"
          fullWidth
          label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.prefixedColors')}
          select
          onChange={(event) => {
            const option = PREFIXED_COLORS.find(
              (option) => option.label === event.target.value
            );
            setSelectedShapesMultiple({
              stroke_style: option?.stroke_style,
              fill_style: option?.fill_style,
              fill_pattern: "",
            });
          }}
          sx={{ zIndex: 9999 }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: { zIndex: 9999 },
              },
            },
          }}
          value={prefixedValue ?? "custom"}
        >
          {PREFIXED_COLORS.map((option) => (
            <MenuItem key={option.label} value={option.label}>
              {option.label}
            </MenuItem>
          ))}
          <MenuItem value="custom">{t('settings.rampsSettings.rampEditor.shapeEdit.styling.custom')}</MenuItem>
        </TextField>

        {/* Fill Section */}
        <Accordion defaultExpanded disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />} sx={{ px: 0, minHeight: 28 }}>
            <Typography variant="caption" fontWeight={600}>{t('settings.rampsSettings.rampEditor.shapeEdit.styling.fill')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0, pb: 0.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%">
              <MuiColorInput
                fullWidth
                size="small"
                label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.color')}
                isAlphaHidden={false}
                onChange={(value: string, colors: MuiColorInputColors) => {
                  setSelectedShapesMultiple({
                    fill_style: colors.hex8,
                    fill_pattern: "",
                  });
                }}
                value={shape?.fill_style ?? ""}
              />
              <TextField
                size="small"
                fullWidth
                label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.pattern')}
                select
                onChange={(event) => {
                  setSelectedShapesMultiple({
                    fill_style: "",
                    fill_pattern: event.target.value,
                  });
                }}
                value={shape?.fill_pattern || ""}
              >
                {FILL_PATTERNS().map((option) => (
                  <MenuItem key={option.label} value={option.label}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <img
                        src={option.value}
                        style={{
                          width: 24,
                          height: 24,
                          border: `1px solid darkgrey`,
                        }}
                      />
                      <Typography variant="inherit">
                        {option.label}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Line Section */}
        <Accordion defaultExpanded disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />} sx={{ px: 0, minHeight: 28 }}>
            <Typography variant="caption" fontWeight={600}>{t('settings.rampsSettings.rampEditor.shapeEdit.styling.line')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0, pb: 0.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%">
              <MuiColorInput
                fullWidth
                size="small"
                label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.color')}
                isAlphaHidden={false}
                onChange={(value: string, colors: MuiColorInputColors) => {
                  setSelectedShapes("stroke_style", colors.hex8);
                }}
                value={shape?.stroke_style ?? ""}
              />
              <TextField
                size="small"
                fullWidth
                label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.lineStyle')}
                select
                value={shape?.line_style || "solid"}
                onChange={(event) => {
                  setSelectedShapes("line_style", event.target.value);
                }}
              >
                {[...LINE_STYLES].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.lineWidth')}
                inputProps={{ min: 1, max: 20, step: 1 }}
                onChange={(event) => {
                  setSelectedShapes("line_width", Number(event.target.value));
                }}
                value={shape?.line_width ?? 1}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Text Section */}
        <Accordion defaultExpanded disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />} sx={{ px: 0, minHeight: 28 }}>
            <Typography variant="caption" fontWeight={600}>{t('settings.rampsSettings.rampEditor.shapeEdit.styling.text')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0, pb: 0.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%">
              <TextField
                fullWidth
                size="small"
                type="number"
                label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.fontSize')}
                inputProps={{ min: 5, max: 64, step: 1 }}
                onChange={(event) => {
                  setSelectedShapes("font_size", Number(event.target.value));
                }}
                value={shape?.font_size ?? 12}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Text Label Controls - Line shapes with text only */}
        {shape?.type === "Line" && shape?.text && (
          <>
            <FormControlLabel
              sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
              control={
                <Switch
                  size="small"
                  checked={(shape.metadata as LineMetadata)?.show_text !== false}
                  onChange={(e) => {
                    const updatedShapes = shapes.map((s) => {
                      if (activeShapes.includes(s.id) || s.id === contextMenu?.shapeId) {
                        return {
                          ...s,
                          metadata: {
                            ...s.metadata,
                            show_text: e.target.checked,
                          },
                        };
                      }
                      return s;
                    });
                    setShapes(updatedShapes);
                  }}
                />
              }
              label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.showText')}
            />
            <TextField
              fullWidth
              size="small"
              select
              label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.textPosition')}
              value={(shape.metadata as LineMetadata)?.text_position || "middle"}
              onChange={(e) => {
                const updatedShapes = shapes.map((s) => {
                  if (activeShapes.includes(s.id) || s.id === contextMenu?.shapeId) {
                    return {
                      ...s,
                      metadata: {
                        ...s.metadata,
                        text_position: e.target.value,
                      },
                    };
                  }
                  return s;
                });
                setShapes(updatedShapes);
              }}
              SelectProps={{
                MenuProps: { PaperProps: { sx: { zIndex: 9999 } } },
              }}
            >
              {TEXT_POSITION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}
      </Stack>
    </TabPanel>
  );
};
